import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenViewingTime from 'common/components/RewindModal/Konvas/ShareableScreenViewingTime';
import Slides from 'common/components/RewindModal/Slides';
import { shardUrl } from 'common/tools/network/url';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';

type ScreenViewingTimeProps = {
  data: {
    total_runtime_hours: number;
    total_runtime_hours_phrase: string;
    image_name?: string;
    trackingScreenName?: string;
  };
  onNext: () => void;
  isLastStep: boolean;
  trackingScreenName?: string;
};

const ScreenViewingTimeContent = ({
  data,
  onNext,
  isLastStep,
  isDownloadReady
}: ScreenViewingTimeProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const hours = data?.total_runtime_hours ?? 0;
  const phrase = data?.total_runtime_hours_phrase ?? '';

  const imageUrl = useMemo(() => {
    const path = `/commons/v9/allocine/flashback/${data.image_name}.png`;
    return shardUrl(path);
  }, [data?.image_name]);

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'mon-temps-de-visionnage-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div className={styles.contentLayout}>
          <div className={styles.timeViewed}>
            <div
              className={classNames(styles.timeViewedHours, styles.golden)}
              style={{ '--target-num': hours } as React.CSSProperties}
            >
              h
            </div>
            <div className={classNames(styles.timeViewedText, styles.golden)}>
              de visionnage
            </div>
          </div>
          <div className={styles.slideText}>{phrase}</div>

          <div className={styles.polaroidImageWrapper}>
            <img
              src={imageUrl}
              alt="Statistique de visionnage"
              className={styles.polaroidImage}
            />
          </div>
        </div>
        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={isDownloading ? 'Génération...' : "Partager cette stat'"}
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenViewingTime ref={stageRef} data={data} />
    </>
  );
};

const ScreenViewingTime = (props: ScreenViewingTimeProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenViewingTimeContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenViewingTime;
